/**
 * 학원 찾기 — 학부모가 학원을 찾아 가입 신청
 *
 * 승인된 학원 목록 → [가입 신청] → 해당 학원(원장)이 승인하면 구성원이 됨.
 * (홈 디렉토리의 간이 버전 — 추후 카테고리·지도와 통합)
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';
import academyService from '../../services/academyService';

const FIELD_LABEL = { study: '📚 교과 과정', sports: '🥋 운동 / 태권도', arts: '🎨 음악 / 미술' };

const AcademyFindPage = () => {
  const { userData } = useAuth();
  const { myAcademies, reloadAcademies } = useAcademy();

  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requested, setRequested] = useState([]);   // 신청 완료한 학원 id
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const list = await academyService.getApprovedAcademies();
        setAcademies(list);
      } catch (e) {
        console.error('학원 목록 로드 실패:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const myIds = myAcademies.map((a) => a.id);

  const handleJoin = async (academy) => {
    if (!userData?.uid) return;
    setBusyId(academy.id);
    try {
      await academyService.joinAcademy(academy.id, userData.uid);
      await reloadAcademies();
      setRequested((prev) => [...prev, academy.id]);
    } catch (e) {
      console.error('학원 가입 실패:', e);
      alert('학원 가입에 실패했습니다.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Container>
      <Header>학원 찾기</Header>
      <Sub>가입 신청하면 학원에서 승인 후 구성원이 됩니다.</Sub>

      {loading ? (
        <Hint>불러오는 중...</Hint>
      ) : academies.length === 0 ? (
        <Hint>등록된 학원이 없습니다.</Hint>
      ) : (
        <List>
          {academies.map((a) => {
            const joined = myIds.includes(a.id);
            const reqDone = requested.includes(a.id);
            return (
              <Item key={a.id}>
                <Thumb $url={a.photoUrl} />
                <Info>
                  <Name>{a.name}</Name>
                  <Meta>{FIELD_LABEL[a.field] || a.field} · {a.subject}</Meta>
                  {a.location && <Meta>{a.location}</Meta>}
                </Info>
                {joined ? (
                  <StateText>가입됨</StateText>
                ) : reqDone ? (
                  <StateText>가입됨</StateText>
                ) : (
                  <JoinBtn
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => handleJoin(a)}
                  >
                    {busyId === a.id ? '가입 중' : '가입하기'}
                  </JoinBtn>
                )}
              </Item>
            );
          })}
        </List>
      )}
    </Container>
  );
};

export default AcademyFindPage;

const Container = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 16px 80px;
`;

const Header = styled.p`
  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const Sub = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 6px 0 18px;
`;

const Hint = styled.p`
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.hint};
  padding: 40px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
`;

const Thumb = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 10px;
  background: ${({ $url, theme }) =>
    $url ? `url(${$url}) center/cover` : theme.colors.background.default};
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const Name = styled.p`
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Meta = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-top: 2px;
`;

const JoinBtn = styled.button`
  flex-shrink: 0;
  padding: 9px 14px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  &:disabled { opacity: 0.5; }
`;

const StateText = styled.span`
  flex-shrink: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.accent.gold};
`;
